import { useState } from "react";
import { businessListings } from "@/mocks/marketplace";

const typeColors: Record<string, string> = {
  "For Sale": "bg-green-100 text-green-700",
  "Partnership": "bg-amber-100 text-amber-700",
  "Wanted": "bg-rose-100 text-rose-700",
};

export default function MarketplaceSection() {
  const [activeType, setActiveType] = useState("All");

  const filtered = activeType === "All"
    ? businessListings
    : businessListings.filter((b) => b.type === activeType);

  return (
    <section id="marketplace" className="py-16 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-amber-500 text-sm font-semibold uppercase tracking-wider">Commerce</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">Buy &amp; Sell Business Ads</h2>
          </div>
          <button className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors cursor-pointer whitespace-nowrap w-fit">
            + List Your Business
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {["All", "For Sale", "Partnership", "Wanted"].map((type) => (
            <button
              key={type}
              onClick={() => setActiveType(type)}
              className={`text-sm font-medium px-5 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeType === type
                  ? "bg-amber-500 text-white"
                  : "bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* Listings Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((biz) => (
            <div
              key={biz.id}
              className="bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-amber-200 hover:shadow-sm transition-all duration-200 cursor-pointer group"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={biz.image}
                  alt={biz.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className={`absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full ${typeColors[biz.type] || "bg-stone-100 text-stone-700"}`}>
                  {biz.type}
                </span>
                <span className="absolute top-3 right-3 bg-white text-green-700 font-bold text-sm px-3 py-1 rounded-full">
                  {biz.price}
                </span>
              </div>
              <div className="p-4">
                <span className="text-xs text-amber-600 font-medium">{biz.category}</span>
                <h3 className="font-semibold text-gray-900 text-sm mt-1 mb-1 line-clamp-2">{biz.title}</h3>
                <p className="text-stone-500 text-xs leading-relaxed mb-3 line-clamp-2">{biz.description}</p>
                <div className="flex items-center gap-3 mb-3 text-xs text-stone-400">
                  <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{biz.location}</span>
                  {biz.revenue !== "N/A" && (
                    <span className="flex items-center gap-1"><i className="ri-line-chart-line text-green-500"></i>{biz.revenue}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button className="flex-1 text-xs font-semibold py-2 rounded-lg border border-amber-400 text-amber-600 hover:bg-amber-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    Contact Seller
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-stone-200 text-stone-400 hover:text-amber-500 hover:border-amber-300 transition-colors cursor-pointer">
                    <i className="ri-bookmark-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
